import { Link } from "react-router-dom";
import { Section, Card, CTA } from "@/components/Section";
import { ArrowRight, Bot, FileText, Presentation, Globe, Workflow, Search } from "lucide-react";

const AGENT_EXAMPLES = [
  "Leadership planning agents",
  "Communication agents",
  "Research agents",
  "Grant-development systems",
  "Operations agents",
  "Client-service assistants",
  "Training and onboarding assistants",
  "Data-analysis assistants",
];

const DIGITAL = [
  { icon: Presentation, title: "PowerPoint & Presentation Design", to: "/quote?service=PowerPoint%20Presentation",
    body: "Conference, keynote, board, proposal, training, sales, community, and special-occasion decks, plus speaker notes and handouts." },
  { icon: FileText, title: "Pitch Deck Development", to: "/quote?service=Pitch%20Deck",
    body: "Investor, funding, sales, partnership, real estate, sponsorship, nonprofit, and project-proposal decks that make the ask clear." },
  { icon: Globe, title: "Website Design & Development", to: "/quote?service=Website%20Design%20and%20Development",
    body: "Professional, responsive, accessible websites for organizations, programs, and events." },
  { icon: Workflow, title: "Workflow Design & Automation", to: "/quote?service=Workflow%20Design%20or%20Automation",
    body: "Practical AI-assisted workflows and templates that reduce administrative burden." },
  { icon: Search, title: "Research, Analysis & Executive Briefs", to: "/quote?service=Research%20and%20Executive%20Brief",
    body: "Decision-ready synthesis, competitive reviews, and executive briefing documents." },
];

export default function AISolutions() {
  return (
    <div data-testid="page-ai-solutions">
      <Section eyebrow="AI Agents & Digital Solutions"
        title="Custom AI agents, super agents, and digital tools, built around your work."
        kicker="An AI agent is a customized assistant designed for a specific role or workflow. A super-agent system coordinates multiple specialized functions across a more complex process. Important outputs remain under human review.">

        <div className="grid md:grid-cols-12 gap-6">
          <Card className="md:col-span-7">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--layor-navy)] text-[var(--layor-gold)] flex-shrink-0"><Bot size={20} /></div>
              <div className="flex-1">
                <div className="eyebrow">Meet Your Agents</div>
                <h3 className="mt-2 font-display text-2xl text-[var(--layor-navy)] leading-snug">Examples of agents we build</h3>
              </div>
            </div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2">
              {AGENT_EXAMPLES.map((e, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[var(--layor-navy)]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--layor-gold)]" /> {e}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/quote?service=AI%20Agent%20or%20Super%20Agent" className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="ai-agent-quote">
                Ask About an AI Agent <ArrowRight size={14} />
              </Link>
              <Link to="/contact" className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold">Book a Consultation</Link>
            </div>
          </Card>

          <Card className="md:col-span-5 text-white border-transparent relative overflow-hidden" style={{ backgroundColor: "var(--layor-navy)" }}>
            <div className="absolute -top-8 -right-8 opacity-30" aria-hidden="true">
              <svg width="180" height="180" viewBox="0 0 200 200" fill="none">
                <rect x="60" y="55" width="80" height="70" rx="14" stroke="#C6A85A" strokeWidth="3" />
                <circle cx="82" cy="85" r="6" fill="#C6A85A" />
                <circle cx="118" cy="85" r="6" fill="#E33278" />
                <rect x="85" y="105" width="30" height="4" rx="2" fill="#C6A85A" />
                <line x1="100" y1="55" x2="100" y2="35" stroke="#C6A85A" strokeWidth="3" />
                <circle cx="100" cy="30" r="6" fill="#E33278" />
                <line x1="60" y1="90" x2="45" y2="90" stroke="#C6A85A" strokeWidth="3" />
                <line x1="140" y1="90" x2="155" y2="90" stroke="#C6A85A" strokeWidth="3" />
                <rect x="70" y="125" width="60" height="30" rx="6" stroke="#C6A85A" strokeWidth="3" />
              </svg>
            </div>
            <div className="relative">
              <div className="eyebrow" style={{ color: "#E6C871" }}>Responsible AI</div>
              <h3 className="mt-3 font-display text-xl text-white leading-snug font-semibold">AI supports leadership, it does not replace it.</h3>
              <p className="mt-3 text-white text-sm leading-relaxed font-medium">
                Every solution is designed for human oversight, transparent use, and organizational fit. We do not describe AI as a replacement for judgment, and we do not build systems that operate without accountability.
              </p>
            </div>
          </Card>
        </div>
      </Section>

      <Section eyebrow="Digital Solutions" title="Beyond agents, the full studio."
        className="bg-white border-y border-[var(--layor-border)]">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {DIGITAL.map((d, i) => {
            const Icon = d.icon;
            return (
              <Card key={i} data-testid={`digital-${i}`}>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--layor-blue)]/10 text-[var(--layor-blue)]"><Icon size={18} /></div>
                <h3 className="mt-4 font-display text-lg text-[var(--layor-navy)]">{d.title}</h3>
                <p className="mt-2 text-sm text-[var(--layor-muted)] leading-relaxed">{d.body}</p>
                <Link to={d.to} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--layor-navy)]">
                  Request a Quote <ArrowRight size={14} />
                </Link>
              </Card>
            );
          })}
        </div>
      </Section>

      {/* AI OPERATIONAL ASSESSMENT */}
      <Section eyebrow="AI Operational Assessment" title="Where can AI save your organization time, money, and leadership capacity, without sacrificing quality, confidentiality, or human judgment?"
        kicker="Organizations do not simply need someone to explain ChatGPT. They need a partner who can walk into the organization, identify repetitive work and bottlenecks, and answer that question with a practical plan."
        className="bg-white border-y border-[var(--layor-border)]">
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            "Executive communication",
            "Meeting preparation",
            "Research and analysis",
            "Policy and document review",
            "Reporting and dashboards",
            "Presentations and decks",
            "Staff onboarding",
            "Project management",
            "Stakeholder communication",
            "Grant workflows",
            "Compliance and accountability",
            "Administrative processes",
          ].map((area, i) => (
            <div key={area} className="lift-card bg-white border border-[var(--layor-border)] rounded-xl px-4 py-4 flex items-start gap-3" data-testid={`assess-area-${i}`}>
              <span className="mt-1 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#C6A85A" }} />
              <span className="text-sm text-[var(--layor-navy)] font-medium">{area}</span>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/quote?service=AI%20Consulting" className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold" data-testid="assess-cta">
            Request an AI Operational Assessment <ArrowRight size={14} />
          </Link>
          <Link to="/contact" className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold">Book a Consultation</Link>
        </div>
      </Section>

      {/* METHODOLOGY: Assess → Design → Build → Train → Support */}
      <section className="py-16 md:py-24 relative overflow-hidden" style={{ backgroundColor: "var(--layor-navy)" }}>
        <div className="absolute inset-0 layr-noise" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
          <div className="max-w-3xl mb-10">
            <div className="eyebrow font-semibold" style={{ color: "#E6C871" }}>The LAYOR Methodology</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05]">Assess. Design. Build. Train. Support.</h2>
            <p className="mt-5 text-white/85 leading-relaxed">
              A repeatable five-step methodology for turning AI curiosity into AI transformation, without adding another initiative for leaders to manage.
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { n: "01", t: "Assess", b: "Walk the organization, identify repetitive work, bottlenecks, and highest-leverage opportunities." },
              { n: "02", t: "Design", b: "Map practical AI systems around real leadership responsibilities and organizational priorities." },
              { n: "03", t: "Build", b: "Develop custom agents, workflows, prompts, and templates the team can actually use." },
              { n: "04", t: "Train", b: "Equip leaders and teams to operate the systems confidently, with human oversight built in." },
              { n: "05", t: "Support", b: "Ongoing advisory retainer, iteration, and expansion as the organization matures." },
            ].map((s, i) => (
              <div key={s.n} className="rounded-2xl border p-5" style={{ borderColor: "rgba(230,200,113,0.35)", backgroundColor: "rgba(255,255,255,0.06)" }} data-testid={`method-${i}`}>
                <div className="font-display-black text-3xl" style={{ color: "#E6C871" }}>{s.n}</div>
                <h3 className="mt-2 font-display text-lg text-white font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-white leading-relaxed font-medium">{s.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-white/70 max-w-3xl">
            LAYOR partners through transformation packages and ongoing advisory retainers so leaders and teams capture compounding value over time.
          </p>
        </div>
      </section>

      {/* BEYOND EDUCATION */}
      <Section eyebrow="Cross-Industry Reach" title="Rooted in executive experience, applied across industries.">
        <p className="text-[var(--layor-muted)] max-w-3xl leading-relaxed">
          LAYOR's methodology was shaped inside senior executive leadership, where the stakes are high and the workload is relentless. That same methodology carries cleanly into any organization managing similar complexity.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            "Professional-services firms",
            "Financial and insurance organizations",
            "Nonprofit organizations",
            "Associations and chambers",
            "Established small businesses",
            "Real estate and investment firms",
          ].map((seg, i) => (
            <div key={seg} className="lift-card bg-white border border-[var(--layor-border)] rounded-xl px-4 py-4 flex items-start gap-3" data-testid={`beyond-${i}`}>
              <span className="mt-1 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#E33278" }} />
              <span className="text-sm text-[var(--layor-navy)] font-medium">{seg}</span>
            </div>
          ))}
        </div>
      </Section>

      <CTA
        headline="Start with one project. Build capacity from there."
        sub="Whether it is a single AI agent or a full workflow redesign, we scope every project around your team's real work."
        actions={
          <>
            <Link to="/quote" className="btn-primary rounded-full px-5 py-3 text-sm font-semibold">Request a Quote</Link>
            <Link to="/contact" className="btn-gold rounded-full px-5 py-3 text-sm font-semibold">Book a Consultation</Link>
          </>
        }
      />
    </div>
  );
}
